import React from "react"
import gql from "graphql-tag"
import { graphql } from "@apollo/client/react/hoc"
import Loader from "./Loader"
import withApolloClient from "../withApolloClient"

const CurrentUserQuery = gql`
  query CurrentUserQuery {
    currentUser {
      id
      member {
        id
        displayFullUsername
        branches {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`

const memberTablePreference = gql`
  query memberTablePreference($memberId: ID!, $tableId: String!) {
    memberTablePreference(memberId: $memberId, tableId: $tableId)
  }
`

const ManagePreferenceHOC =
    (tableId, tableHeaders = []) =>
        (WrappedComponent) => {
            if (typeof WrappedComponent !== "function" && typeof WrappedComponent !== "object") {
                return () => null
            }

            class GraphQLContainer extends React.Component {
                constructor(props) {
                    super(props)
                    this.state = {
                        columnConfig: tableHeaders || [],
                    }
                }

                componentDidUpdate(prevProps) {
                    const { loading, memberLoading, memberTablePreference } = this.props
                    if (loading || memberLoading) return
                    if (Array.isArray(memberTablePreference) && prevProps.memberTablePreference !== memberTablePreference) {
                        this.processPreferences(memberTablePreference)
                    }
                }

                processPreferences = (preferenceData) => {
                    if (!Array.isArray(preferenceData) || preferenceData.length === 0) return

                    const visibleIds = new Set(preferenceData)

                    const updatedColumns = (tableHeaders || []).map((col) => {
                        return {
                            ...col,
                            visible: visibleIds.has(col.id),
                            order: preferenceData.indexOf(col.id) !== -1 ? preferenceData.indexOf(col.id) : tableHeaders.length,
                        }
                    }).sort((a, b) => a.order - b.order)

                    const prev = this.state.columnConfig
                    const changed =
                        prev.length !== updatedColumns.length ||
                        prev.some((c, i) => c.id !== updatedColumns[i].id || c.visible !== updatedColumns[i].visible || c.order !== updatedColumns[i].order)

                    if (changed) {
                        this.setState({ columnConfig: updatedColumns })
                    }
                }

                refetchPreference = () => {
                    if (typeof this.props.refetch !== "function") return
                    this.props
                        .refetch()
                        .then((response) => {
                            const prefs = response?.data?.memberTablePreference
                            if (Array.isArray(prefs)) {
                                this.processPreferences(prefs)
                            }
                        }).catch((error) => {
                            console.error("Error refetching preferences:", error)
                        })
                }

                render() {
                    const { loading, currentUser, memberLoading, ...rest } = this.props
                    const { columnConfig } = this.state

                    if (loading || memberLoading || !currentUser) return <Loader />

                    return (
                        <WrappedComponent {...rest} columnConfig={columnConfig} refetchPreferences={this.refetchPreference} currentUser={currentUser} />
                    )
                }
            }

            return graphql(CurrentUserQuery, {
                props: ({ data: { currentUser, loading } }) => ({
                    currentUser,
                    loading,
                }),
            })(
                graphql(memberTablePreference, {
                    options: (props) => {
                        const memberId = props?.currentUser?.member?.id
                        return {
                            variables: {
                                memberId,
                                tableId: typeof tableId === "function" ? tableId(props) : tableId,
                            },
                            fetchPolicy: "network-only",
                            notifyOnNetworkStatusChange: true,
                        }
                    },
                    skip: (props) => {
                        const memberId = props?.currentUser?.member?.id
                        return !memberId
                    },
                    props: ({ data: { memberTablePreference, loading, error, refetch } }) => ({
                        memberTablePreference,
                        memberLoading: loading,
                        error,
                        refetch,
                    }),
                })(withApolloClient(GraphQLContainer))
            )
        }

export default ManagePreferenceHOC